import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../lib/shadcn/card'
import { Skeleton } from '../../lib/shadcn/skeleton'
import { TrendingUp } from 'lucide-react'

export interface OrderVolumePoint {
  week: string
  order_count: number
  revenue: number
}

function formatWeek(value: string): string {
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
})

export default function OrderVolumeChart({
  data,
  loading,
}: {
  data: OrderVolumePoint[]
  loading: boolean
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <TrendingUp className="w-4 h-4" />
          Order volume &amp; revenue
        </CardTitle>
        <CardDescription>Orders placed and revenue by week</CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-[320px] w-full" />
        ) : data.length === 0 ? (
          <p className="py-16 text-sm text-muted-foreground text-center">No order history yet.</p>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={data} margin={{ top: 16, right: 8, left: 0, bottom: 8 }}>
              <CartesianGrid vertical={false} strokeDasharray="3 3" strokeOpacity={0.65} stroke="hsl(var(--border))" />
              <XAxis axisLine={false} tickLine={false} tickMargin={10}
                dataKey="week"
                tickFormatter={formatWeek}
                tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
              />
              <YAxis axisLine={false} tickLine={false} tickMargin={10}
                yAxisId="left"
                tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                allowDecimals={false}
              />
              <YAxis axisLine={false} tickLine={false} tickMargin={10}
                yAxisId="right"
                orientation="right"
                tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                tickFormatter={value => currencyFormatter.format(Number(value))}
              />
              <Tooltip
                labelFormatter={(label: unknown) => formatWeek(String(label))}
                formatter={(value: unknown, name: unknown): [string, string] =>
                  name === 'revenue'
                    ? [currencyFormatter.format(Number(value)), 'Revenue']
                    : [String(value), 'Orders']
                }
                contentStyle={{
                  backgroundColor: 'hsl(var(--popover))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: 8,
                  color: 'hsl(var(--popover-foreground))',
                }}
              />
              <Legend
                formatter={value => (value === 'revenue' ? 'Revenue' : 'Orders')}
                wrapperStyle={{ fontSize: 12 }}
              />
              <Bar yAxisId="left" dataKey="order_count" name="order_count" fill="hsl(var(--chart-1))" radius={[4, 4, 0, 0]} />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="revenue"
                name="revenue"
                stroke="hsl(var(--chart-3))"
                strokeWidth={2}
                dot={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  )
}
